import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import {
  Clock, Loader2, ArrowRight, MessageCircle, Video, Phone, Mail,
  XCircle, CreditCard, Ban
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sessionTypeIcons = { chat: MessageCircle, video: Video, call: Phone, email: Mail };
const sessionTypeLabels = { chat: 'Chat', video: 'Vídeo', call: 'Ligação', email: 'Email' };
const statusLabels = {
  pending_payment: 'Aguardando Pagamento', scheduled: 'Agendada',
  in_progress: 'Em Andamento', completed: 'Concluída', cancelled: 'Cancelada'
};
const statusColors = {
  pending_payment: 'bg-[#D4A373]/10 text-[#D4A373]', scheduled: 'bg-[#4A5D4E]/10 text-[#4A5D4E]',
  in_progress: 'bg-[#6B8E7C]/10 text-[#6B8E7C]', completed: 'bg-[#A0A09A]/10 text-[#A0A09A]',
  cancelled: 'bg-[#B85C5C]/10 text-[#B85C5C]'
};

function SessionCard({ session, isProfessional, isPast, onCancelled }) {
  const TypeIcon = sessionTypeIcons[session.session_type] || MessageCircle;
  const bgColor = isPast ? 'bg-[#A0A09A]/10' : 'bg-[#4A5D4E]/10';
  const iconColor = isPast ? 'text-[#A0A09A]' : 'text-[#4A5D4E]';
  const [cancelling, setCancelling] = useState(false);

  const canCancel = !isPast && ['pending_payment', 'scheduled'].includes(session.status);
  const canPay = session.status === 'pending_payment' && session.checkout_url;

  const handleCancel = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja cancelar esta sessão?')) return;
    setCancelling(true);
    try {
      await axios.put(`${API_URL}/api/sessions/${session.session_id}/cancel`, {}, {
        headers: getAuthHeaders(), withCredentials: true
      });
      toast.success('Sessão cancelada com sucesso');
      if (onCancelled) onCancelled(session.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar sessão');
    } finally {
      setCancelling(false);
    }
  };

  const handlePay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (session.checkout_url) {
      window.location.href = session.checkout_url;
    }
  };

  return (
    <div
      className={`card-theraflow p-5 ${isPast ? 'opacity-75' : ''}`}
      data-testid={`${isPast ? 'past-' : ''}session-${session.session_id}`}
    >
      <Link
        to={`/session/${session.session_id}`}
        className={`flex items-center gap-4 ${!isPast ? 'hover:opacity-80' : ''} transition-opacity`}
      >
        <div className={`w-12 h-12 rounded-xl ${bgColor} flex items-center justify-center flex-shrink-0`}>
          <TypeIcon className={`w-6 h-6 ${iconColor}`} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-[#292927] truncate">
            {isProfessional ? session.user_name : session.professional_name}
          </p>
          <p className="text-sm text-[#63635E]">
            {sessionTypeLabels[session.session_type]} - {session.duration_minutes} min
          </p>
          {!isPast && session.scheduled_at && (
            <p className="text-xs text-[#A0A09A] mt-1">
              {new Date(session.scheduled_at).toLocaleString('pt-BR')}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full ${statusColors[session.status] || 'bg-gray-100'}`}>
            {statusLabels[session.status] || session.status}
          </span>
          <span className={`text-sm ${isPast ? 'text-[#63635E]' : 'font-medium text-[#292927]'}`}>
            R$ {session.price?.toFixed(2)}
          </span>
        </div>
      </Link>

      {/* Action Buttons */}
      {!isPast && (canCancel || canPay) && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#EAE8E3]">
          {canPay && (
            <button
              onClick={handlePay}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-[#4A5D4E] text-white text-sm font-medium hover:bg-[#3A4A3E] transition-colors"
              data-testid={`pay-now-${session.session_id}`}
            >
              <CreditCard className="w-4 h-4" strokeWidth={1.5} />
              Pagar Agora
            </button>
          )}
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-[#B85C5C]/30 text-[#B85C5C] text-sm font-medium hover:bg-[#B85C5C]/5 transition-colors"
              data-testid={`cancel-session-${session.session_id}`}
            >
              {cancelling ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Ban className="w-4 h-4" strokeWidth={1.5} />
                  Cancelar
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SessionsSection({ loading, upcomingSessions, pastSessions, isProfessional, onSessionCancelled }) {
  return (
    <div className="space-y-8">
      <div data-testid="upcoming-sessions">
        <h2 className="text-xl font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Próximas Sessões
        </h2>
        {loading ? (
          <div className="card-theraflow p-8 flex justify-center">
            <Loader2 className="w-6 h-6 text-[#4A5D4E] animate-spin" />
          </div>
        ) : upcomingSessions.length === 0 ? (
          <div className="card-theraflow p-8 text-center">
            <Clock className="w-12 h-12 mx-auto text-[#A0A09A] mb-3" strokeWidth={1} />
            <p className="text-[#63635E] mb-4">Nenhuma sessão agendada</p>
            <Link to="/professionals" className="btn-primary inline-flex items-center gap-2 text-sm">
              Agendar Sessão <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingSessions.map((s) => (
              <SessionCard key={s.session_id} session={s} isProfessional={isProfessional} isPast={false} onCancelled={onSessionCancelled} />
            ))}
          </div>
        )}
      </div>

      {pastSessions.length > 0 && (
        <div data-testid="past-sessions">
          <h2 className="text-xl font-medium text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Sessões Anteriores
          </h2>
          <div className="space-y-3">
            {pastSessions.slice(0, 5).map((s) => (
              <SessionCard key={s.session_id} session={s} isProfessional={isProfessional} isPast={true} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
